import axios from 'axios';
import KeycloakService from './KeycloakService';
import { API_INTERNAL_BASE_URL, API_TIMEOUT, ENABLE_API_LOGGING } from '../config/api';

class BaseInternalApiService {
  /**
   * Create axios instance with authentication headers for internal API
   * @returns {Object} Axios instance
   */
  getAxiosInstance = () => {
    const axiosInstance = axios.create({
      baseURL: API_INTERNAL_BASE_URL,
      timeout: API_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    axiosInstance.interceptors.request.use((config) => {
      const token = KeycloakService.getToken();
      if (token) {
        if (ENABLE_API_LOGGING) {
          console.log('Adding auth token to internal API request:', config.url);
        }
        config.headers.Authorization = `Bearer ${token}`;
      } else if (ENABLE_API_LOGGING) {
        console.warn('No token available for internal API request:', config.url);
      }
      return config;
    });

    axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (ENABLE_API_LOGGING) {
          console.error('Internal API error response:', error.response?.status, error.config?.url);
        }
        if (error.response && error.response.status === 401) {
          if (ENABLE_API_LOGGING) {
            console.log('Received 401 from internal API, attempting to refresh token');
          }
          return KeycloakService.getKeycloak().updateToken(5)
            .then(() => {
              if (ENABLE_API_LOGGING) {
                console.log('Token refreshed, retrying internal API request');
              }
              const originalRequest = error.config;
              originalRequest.headers.Authorization = `Bearer ${KeycloakService.getToken()}`;
              return axios(originalRequest);
            })
            .catch((refreshError) => {
              if (ENABLE_API_LOGGING) {
                console.error('Failed to refresh token for internal API:', refreshError);
              }
              KeycloakService.logout();
              return Promise.reject(error);
            });
        }
        return Promise.reject(error);
      }
    );

    return axiosInstance;
  };
}

export default BaseInternalApiService;